import { useMutation, useQueryClient } from '@tanstack/react-query'

import { API_URL } from '@/constants/env'

import { insertAtBeginningOfPage } from '@/helpers/api'
import { SweetspotError } from '@/helpers/error'

import { useAuthInfoWithThrow } from '@/hooks/user/useAuthInfoWithThrow'
import { wordTemplatesQueryKeys } from '@/hooks/word-templates/types'

import type { PaginatedResponse } from '@/types/Api'
import type { File as UploadedFile } from '@/types/File'
import type { UUID } from '@/types/Id'

interface UploadWordTemplatesArgs {
  orgId: UUID | ''
}

export interface UploadWordTemplateRequest {
  file: File
  tag_ids: UUID[]
  cover_pages: number
  unrestricted: boolean
}

export interface UploadWordTemplatesMutationArgs {
  templates: UploadWordTemplateRequest[]
}

const uploadWordTemplates = async (
  orgId: UUID | '',
  req: UploadWordTemplatesMutationArgs,
  accessToken: string,
): Promise<Array<UploadedFile>> => {
  if (!orgId) {
    throw new Error('No organization ID provided.')
  }
  // TODO: implement backend so it can accept multiple files at once;
  // relying on these promises isn't great since it acts as the source of truth,
  // when the backend should always be the source of truth instead
  const uploadPromises = req.templates.map(async (template) => {
    const formData = new FormData()
    formData.append('file', template.file)
    const url = new URL(
      `${API_URL}/api/v1/organizations/${orgId}/word-templates`,
    )
    if (template.tag_ids.length > 0) {
      url.searchParams.set(
        'tag_ids',
        template.tag_ids.map((id) => id.toString()).join(','),
      )
    }
    if (template.cover_pages > 0) {
      url.searchParams.set('cover_pages', template.cover_pages.toString())
    }
    if (template.unrestricted !== undefined) {
      url.searchParams.set(
        'unrestricted',
        template.unrestricted ? 'true' : 'false',
      )
    }
    return fetch(url, {
      method: 'POST',
      body: formData,
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }).then(async (res) => {
      if (!res.ok) {
        throw new SweetspotError({
          name: 'UPLOAD_FILE_ERROR',
          message: res.statusText,
          cause: {
            status: res.status,
          },
        })
      }
      return (await res.json()) as UploadedFile
    })
  })
  return await Promise.all(uploadPromises)
}

export const useUploadWordTemplates = ({ orgId }: UploadWordTemplatesArgs) => {
  const { accessToken } = useAuthInfoWithThrow()
  const queryClient = useQueryClient()

  return useMutation<
    UploadedFile[],
    Error,
    UploadWordTemplatesMutationArgs,
    PaginatedResponse<UploadedFile>
  >({
    mutationFn: (req) => uploadWordTemplates(orgId, req, accessToken),
    onError: (err, req, context) => {
      // rollback to the previous value
      const key = wordTemplatesQueryKeys.organization({ orgId })
      queryClient.setQueryData(key, context)
    },
    onSuccess: (data, req) => {
      const key = wordTemplatesQueryKeys.organization({ orgId })
      queryClient.setQueryData<PaginatedResponse<UploadedFile>>(
        key,
        (oldData) => {
          const result = insertAtBeginningOfPage(
            { data, count: data.length, has_more: false },
            oldData,
          )
          return result
        },
      )
      queryClient.invalidateQueries({ queryKey: key })
      return data
    },
  })
}
